/**
 * The minute or two after "Build the agent", shown honestly.
 *
 * Every line here comes off the build row the Workflow writes as it goes (`stage`,
 * `pagesTotal`, `pagesDone`) - nothing is on a timer pretending to be progress. That matters
 * because a big site that takes three minutes and a run that died look the same from outside;
 * counting real pages tells them apart, and after four quiet minutes the screen asks the
 * Workflow itself (`?run=1`) whether the run is still alive.
 *
 * DURABLE: the polling lives in an effect keyed on the agent, so a reload or a second tab picks
 * the same run up. Ported from demo-creator's `DraftProgress.tsx`.
 *
 * WHEN IT IS DONE, SETTINGS IS THE REVIEW. With nothing to flag this goes straight there; with
 * something (an action the validators refused, no hours found, a line that looks like a fact)
 * it lists it first, because Settings would not say.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api, post, type BuildProgress, type BuildStage } from "../api";
import { PageHead } from "../components/Page";
import { Empty } from "../components/States";
import { Button, CardHead, SettingCard, StatusPill, cx } from "../ui";
import { useAgent, useCurrentAgent } from "../currentAgent";
import { useScopedTo } from "../components/Shell";

const POLL_MS = 1500;
/** After this long with the row still running, ask the Workflow itself (one RPC). */
const STALL_MS = 4 * 60_000;

const STAGES: Array<{ key: BuildStage; label: string; done: string }> = [
  { key: "discovering", label: "Finding their pages", done: "Found their pages" },
  { key: "reading", label: "Reading their site", done: "Read their site" },
  { key: "writing", label: "Writing the agent", done: "Wrote the agent" },
  { key: "saving", label: "Setting it up", done: "Set it up" },
];

export function useBuildProgress(agentId: string, attempt: number) {
  const [build, setBuild] = useState<BuildProgress | null>(null);
  const [missing, setMissing] = useState(false);
  const [offline, setOffline] = useState(false);
  const [dead, setDead] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let misses = 0;
    let asked = false;
    const from = Date.now();

    async function tick(): Promise<void> {
      try {
        const stalled = !asked && Date.now() - from > STALL_MS;
        const body = await api<{ build: BuildProgress; run?: string }>(
          `/v1/build?agent=${encodeURIComponent(agentId)}${stalled ? "&run=1" : ""}`,
        );
        if (cancelled) return;
        misses = 0;
        setOffline(false);
        setBuild(body.build);
        if (body.build.status !== "running") return;
        if (stalled) {
          asked = true;
          // The row says running but the instance is gone, so nothing will ever write the
          // failure onto it. Say it here instead of spinning until somebody gives up.
          if (!body.run || ["errored", "terminated", "complete", "unknown"].includes(body.run)) {
            setDead(true);
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setMissing(true);
          return;
        }
        misses += 1;
        if (misses >= 3) setOffline(true);
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS);
    }

    setDead(false);
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [agentId, attempt]);

  return { build, missing, offline, dead };
}

/** Whether a finished build has anything to say that Settings would not. */
export function needsLook(build: BuildProgress): boolean {
  const report = build.report;
  if (!report) return false;
  // Several locations are worth a look even when nothing went wrong: which places the site
  // named, and whose hours it gave which, is the part of a build most likely to be half right.
  return report.dropped.length > 0 || report.warnings.length > 0 || report.pagesRead === 0 || (report.locations ?? 0) > 1;
}

export function AgentBuild() {
  const agentId = useAgent().id;
  const to = useScopedTo();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [retryError, setRetryError] = useState<string | null>(null);
  const { build, missing, offline, dead } = useBuildProgress(agentId, attempt);
  const { reload } = useCurrentAgent();

  const settings = to("/settings?tab=instructions");

  const done = build?.status === "done";
  // The build renames the agent from what the site calls it, so the bar's switcher, read
  // when the agent was created, is re-read once it is done.
  useEffect(() => {
    if (done) void reload();
  }, [done, reload]);

  useEffect(() => {
    if (build?.status === "done" && !needsLook(build)) navigate(settings, { replace: true });
  }, [build, navigate, settings]);

  if (missing) {
    return <Empty title="Nothing is being built here" body="This agent was not built from a website." action={{ label: "Build one", to: "/agents/new" }} />;
  }

  const failed = dead || build?.status === "failed";

  async function retry() {
    setRetryError(null);
    try {
      await post("/v1/build/retry", { agent: agentId });
      setAttempt((n) => n + 1);
    } catch (err) {
      setRetryError(err instanceof ApiError ? err.message : "It could not be started again. Try in a moment.");
    }
  }

  const host = build ? build.websiteUrl.replace(/^https:\/\//, "").replace(/\/$/, "") : "";

  return (
    <>
      <PageHead title={done ? "The agent is built" : "Building the agent"} lede={host ? `From ${host}. This usually takes a minute or two.` : undefined} />
      <div className="mx-auto grid w-full max-w-[720px] gap-5">
        <SettingCard>
          <CardHead
            title={failed ? "It stopped" : build?.status === "done" ? "Done" : "Working on it"}
            aside={
              failed ? (
                <StatusPill tone="alert">Stopped</StatusPill>
              ) : build?.status === "done" ? (
                <StatusPill tone="good">Built</StatusPill>
              ) : (
                <StatusPill tone="warm">Running</StatusPill>
              )
            }
          />
          <ol className="grid gap-3 px-6 py-5">
            {STAGES.map((stage, index) => {
              const current = build?.stage ? STAGES.findIndex((s) => s.key === build.stage) : build?.status === "done" ? STAGES.length : -1;
              const done = index < current;
              const now = index === current && !failed;
              const reading = stage.key === "reading" && build?.pagesTotal ? ` (${Math.min(build.pagesDone, build.pagesTotal)} of ${build.pagesTotal})` : "";
              return (
                <li key={stage.key} className={cx("flex items-center gap-3 text-sm", done ? "text-ink-soft" : now ? "font-medium text-ink" : "text-ink-faint")}>
                  <span
                    aria-hidden="true"
                    className={cx(
                      "size-2.5 flex-none rounded-full",
                      done ? "bg-good" : now ? "animate-pulse bg-accent" : "bg-line-strong",
                    )}
                  />
                  {done ? stage.done : stage.label}
                  {(now || done) && reading}
                </li>
              );
            })}
          </ol>
          {offline && <p className="border-t border-line-soft px-6 py-3 text-sm text-warm">Can't reach the dashboard. Still trying.</p>}
        </SettingCard>

        {failed && (
          <SettingCard className="grid gap-3 p-6">
            <p className="text-sm text-ink">
              {dead ? "The build stopped without saying why." : friendlyBuildError(build?.error ?? null)}
            </p>
            {build?.error && <p className="font-mono text-xs text-ink-faint">{build.error}</p>}
            {retryError && <p role="alert" className="text-sm text-alert">{retryError}</p>}
            <div className="flex gap-2">
              <Button variant="primary" onClick={() => void retry()}>
                Try again
              </Button>
              <Button onClick={() => navigate(settings)}>Open it in Settings</Button>
            </div>
          </SettingCard>
        )}

        {build?.status === "done" && build.report && needsLook(build) && (
          <SettingCard>
            <CardHead title="Worth a look" note={`${build.report.pagesRead} ${build.report.pagesRead === 1 ? "page" : "pages"} saved to Knowledge.`} />
            <ul className="grid gap-2 px-6 py-5 text-sm text-ink-soft">
              {build.report.dropped.map((drop) => (
                <li key={drop.name}>
                  An action called <span className="font-mono text-ink">{drop.name || "(no name)"}</span> was left out: {drop.reason}
                </li>
              ))}
              {build.report.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
              {(build.report.locations ?? 0) > 1 && (
                <li>The site lists {build.report.locations} locations. Check each one's hours and closures under Hours.</li>
              )}
              {build.report.pagesRead === 0 && <li>No page of the site could be read, so the agent knows only what you wrote.</li>}
            </ul>
            <div className="border-t border-line-soft px-6 py-4">
              <Button variant="primary" onClick={() => navigate(settings)}>
                Review in Settings
              </Button>
            </div>
          </SettingCard>
        )}
      </div>
    </>
  );
}

/** What the Workflow threw, said to our team; the raw text is shown under it. */
export function friendlyBuildError(raw: string | null): string {
  const text = raw ?? "";
  if (/No page on .* could be read/i.test(text)) {
    return "Nothing on that site could be read. Some sites turn readers away. Add what you know in the notes, or paste their pages into Knowledge.";
  }
  if (/KEENABLE_API_KEY|OPENAI_API_KEY/.test(text)) return "The Worker is missing its keys. That is a setup fix, not a problem with this site.";
  if (/Keenable/i.test(text)) return "The page reader turned us away (its quota or its key). Try again later.";
  if (/OpenAI answered (429|5\d\d)/i.test(text)) return "The model was busy. Give it a minute and try again.";
  if (/refused/i.test(text)) return "The model would not write this agent. Try again, or add a note about what they do.";
  return text || "The build stopped without saying why.";
}
